import { useContext, useEffect, useState } from "react";

// Services
import useCountryAxiosInstance from "../../../hooks/useCountryAxiosInstance";
import { NotificationsContext } from "../../../ui-components/Notifications";
import CountryConfigContext from "../../../CountryConfigContext";
import { FormatNumberOptions, useIntl } from "react-intl";

// Components
import KpiWorksiteBarChart from "../../../components/Charts/KpiWorksiteBarChart";
import KpiWorksiteDonut from "../../../components/Charts/KpiWorksiteDonut";
import WorksiteTrendChart from "./WorksiteTrendChart";
import UsersHeatMap from "../Users/UsersHeatMap";
import WorksitesDetailTable from "./DetailTable";

//Ui
import SearchSelect from "../../../ui-components/SearchSelect";
import { PageSection } from "../../../ui-components/Container";
import Placeholder from "../../../ui-components/Placeholder";
import { COLORS, lostWorksiteColors } from "../../../components/Charts/commons";
import Toggle from "../../../ui-components/Toggle";
import Input from "../../../ui-components/Input";

// Types
import {
  AnalyticsWorksitesProps,
  ChartWorksite,
  WorksiteRow,
  TrendData,
  GeoData,
  LostWorksiteData,
} from "./Worksites.type";
import SankeyChart from "./SankeyChart";
export const statusOptions = ["planning", "design", "open", "closed"];
const ownershipOptions = [
  "creator",
  "promoter",
  "from_internal",
  "from_external",
];
const currencyOption: FormatNumberOptions = {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
};
const dimensionOrder = {
  planning: 1,
  design: 2,
  open: 3,
  closed: 4,
  promoter: 5,
  creator: 6,
  from_internal: 7,
  from_external: 8,
};

export function AnalyticsWorksites({
  visibilityUserId,
  analysisPeriod,
  internalUsers,
  worksiteTypes,
}: AnalyticsWorksitesProps) {
  const intl = useIntl();
  const [loading, setLoading] = useState(true);
  const { push } = useContext(NotificationsContext);
  const countryApiRequest = useCountryAxiosInstance();

  const [worksites, setWorksites] = useState<WorksiteRow[] | undefined>(
    undefined
  );
  const [worksiteDimension, setWorksiteDimension] = useState<
    "status" | "ownership"
  >("status");
  const [chartWorksites, setChartWorksites] = useState<
    ChartWorksite[] | undefined
  >(undefined);

  const [selectedStatus, setSelectedStatus] = useState<
    { label: string; value: string }[]
  >([]);
  const [selectedOwnership, setSelectedOwnerships] = useState<
    { label: string; value: string }[]
  >([]);

  const [timeUnit, setTimeUnit] = useState<string>("month");
  const [trend, setTrend] = useState<TrendData[] | undefined>(undefined);
  const [geo, setGeo] = useState<GeoData[] | undefined>(undefined);
  const [sankey, setSankey] = useState<object | undefined>(undefined);
  const [lostWorksiteData, setLostWorksiteData] = useState<
    LostWorksiteData[] | undefined
  >([]);
  const [totalLostWorksite, setTotalLostWorksite] = useState<
    number | undefined
  >(0);

  const [filterAddress, setFilterAddress] = useState<string | undefined>(
    undefined
  );
  const [filterCity, setFilterCity] = useState<string | undefined>(undefined);
  const [filterProvince, setFilterProvince] = useState<string | undefined>(
    undefined
  );
  const [filterWorksiteType, setFilterWorksiteType] = useState([]);
  const [filterServices, setFilterServices] = useState([]);

  const { config } = useContext(CountryConfigContext);

  const services = (config.services as { active: boolean; name: string }[])
    .filter((i) => i.active)
    .map((i) => i.name)
    .sort();

  useEffect(() => {
    if (worksites) {
      setChartWorksites(
        worksites
          .map((i) => ({
            category: intl.formatMessage({ id: i.dimension }),
            value: i.val_worksites,
            amount: i.amt_worksites,
            order: dimensionOrder[i.dimension],
            settings: { fill: COLORS[i.dimension], stroke: "rgb(255,255,255)" },
          }))
          .sort((a, b) => a.order - b.order)
      );
    }
  }, [worksites]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const payload = {
          start_date: analysisPeriod.start,
          end_date: analysisPeriod.end,
          visibility_user_id: visibilityUserId,
          dimension: worksiteDimension,
          internal_users: internalUsers.map((i) => i.id),
          filter_status: selectedStatus.length
            ? selectedStatus.map((i) => i.value)
            : undefined,
          filter_ownership: selectedOwnership.length
            ? selectedOwnership.map((i) => i.value)
            : undefined,
          // TODO add filter for users
        };

        if (filterAddress) payload.address = filterAddress;
        if (filterCity) payload.city = filterCity;
        if (filterProvince) payload.cod_province = filterProvince.toUpperCase();
        if (filterWorksiteType)
          payload.cod_worksite_type = filterWorksiteType.map((i) => i.value);
        if (filterServices)
          payload.services = filterServices.map((i) => i.value);

        const [response, sankeyResponse, lostWorksiteResponse] =
          await Promise.all([
            // @ts-ignore
            countryApiRequest.post("/analytics/worksites", payload),
            // @ts-ignore
            countryApiRequest.post("/analytics/worksites/sankey", payload),
            // @ts-ignore
            countryApiRequest.post(
              "/analytics/worksites/lostWorksites",
              payload
            ),
          ]);

        setWorksites(response.worksites);
        setTimeUnit(response.time_unit);
        setTrend(response.trend);
        setGeo(response.geo);
        setSankey(sankeyResponse);
        setLostWorksiteData(
          lostWorksiteResponse.map((el: LostWorksiteData) => ({
            value: el.value,
            category: intl.formatMessage({ id: el.category }),
            settings: {
              fill: lostWorksiteColors[el.category],
              stroke: "rgb(255,255,255)",
            },
          }))
        );
        setTotalLostWorksite(lostWorksiteResponse.length);
      } catch (error) {
        console.log(error);
        setWorksites([]);
        push({
          title: intl.formatMessage({ id: "server_error" }),
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    visibilityUserId,
    analysisPeriod,
    worksiteDimension,
    selectedOwnership,
    selectedStatus,
    filterAddress,
    filterCity,
    filterProvince,
    filterWorksiteType,
    filterServices,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // function getTimeUnit(end: string, start: string) {
  //   // week or month based on the period of start and end date
  //   // both are strings: here is an example start: "2024-07-05", end: "2024-10-12"
  //   const startDate = new Date(start);
  //   const endDate = new Date(end);
  //   if(!startDate || !endDate) return 'month';
  //   const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
  //   const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30));
  //   let ret = diffMonths > 2 ? "month" : "week";
  //   console.log(ret)
  //   return ret;
  // }

  return (
    <div className="w-full mt-5">
      <div className="flex">
        <div className="flex flex-row gap-3 w-full mb-3">
          <SearchSelect
            label={"Stato"}
            options={statusOptions.map((i) => ({
              value: i,
              label: intl.formatMessage({ id: i }),
            }))}
            onChange={(e: any) => setSelectedStatus(e)}
            value={selectedStatus}
            isMulti={true}
            CustomGroup={undefined}
            loadOptions={undefined}
            CustomOption={undefined}
          />
          <SearchSelect
            label="Ownership"
            options={ownershipOptions.map((i) => ({
              value: i,
              label: intl.formatMessage({ id: i }),
            }))}
            onChange={(e: any) => setSelectedOwnerships(e)}
            value={selectedOwnership}
            isMulti={true}
            CustomGroup={undefined}
            loadOptions={undefined}
            CustomOption={undefined}
          />
        </div>
      </div>
      <h2 className="mb-3">
        {intl.formatMessage({
          id: "worksite_filters",
          defaultMessage: "Filtri di anagrafica cantiere",
        })}
      </h2>
      <div className="flex">
        <div>
          <Input
            label={intl.formatMessage({
              id: "address",
              defaultMessage: "Indirizzo",
            })}
            value={filterAddress}
            onChange={(e: any) => setFilterAddress(e.target.value)}
          />
        </div>
        <div className="ml-4">
          <Input
            label={intl.formatMessage({
              id: "city",
              defaultMessage: "Città",
            })}
            value={filterCity}
            onChange={(e: any) => setFilterCity(e.target.value)}
          />
        </div>
        <div className="ml-4">
          <Input
            label={intl.formatMessage({
              id: "province",
              defaultMessage: "Provincia",
            })}
            value={filterProvince}
            onChange={(e: any) => setFilterProvince(e.target.value)}
          />
        </div>
        <div className="ml-4">
          <SearchSelect
            label={intl.formatMessage({
              id: "worksite_type",
              defaultMessage: "Tipo cantiere",
            })}
            value={filterWorksiteType}
            onChange={(e: any) => setFilterWorksiteType(e)}
            options={worksiteTypes.map((i) => ({
              value: i.cod_worksite_type,
              label: i.des_worksite_type,
            }))}
            isMulti
          />
        </div>
        <div className="ml-4">
          <SearchSelect
            label={intl.formatMessage({
              id: "configurations",
              defaultMessage: "Configurazioni",
            })}
            value={filterServices}
            options={config.services.map((el) => ({
              value: el.name,
              label: el.name,
            }))}
            onChange={(e: any) => setFilterServices(e)}
            isMulti
          />
        </div>
      </div>
      {!loading && chartWorksites && worksiteTypes ? (
        <div>
          <PageSection
            title={
              <span className="font-bold">
                {intl.formatMessage({ id: "worksites" })}
              </span>
            }
            description={undefined}
          >
            <div className="flex flex-row gap-x-2 font-bolder mb-8">
              <span>Stato</span>
              <div className="-mt-5">
                {/*@ts-ignore*/}
                <Toggle
                  checked={worksiteDimension === "ownership"}
                  onChange={() => {
                    setWorksiteDimension(
                      worksiteDimension === "status" ? "ownership" : "status"
                    );
                  }}
                />
              </div>
              <span>Ownership</span>
            </div>
            {chartWorksites.length ? (
              <div className="w-full grid grid-cols-1 lg:grid-cols-2 lg:gap-x-4 gap-y-4">
                <div>
                  <KpiWorksiteDonut
                    id="kpiWorksiteDonut"
                    data={chartWorksites}
                    intl={intl}
                  />
                </div>
                <div>
                  <div>
                    <span className="text-lg text-gray-500 font-bold m-1">
                      {intl.formatMessage({ id: "total_amount_kk" })}
                    </span>
                    <span className="text-lg text-gray-700 font-bold m-1">
                      {intl.formatNumber(
                        chartWorksites.reduce((acc, i) => acc + i.amount, 0),
                        currencyOption
                      )}
                    </span>
                  </div>
                  {chartWorksites.reduce((acc, i) => acc + i.amount, 0) > 0 ? (
                    <KpiWorksiteBarChart
                      id="kpiWorksiteBarChart"
                      data={chartWorksites}
                      intl={intl}
                    />
                  ) : null}
                </div>
              </div>
            ) : (
              <span className="text-lg text-gray-500 my-32">
                {intl.formatMessage({ id: "no_data_available" })}
              </span>
            )}
            <div className="w-full flex flex-col lg:flex-row my-4 items-center">
              <div className="w-full lg:w-2/3">
                {trend ? (
                  <div>
                    <WorksiteTrendChart
                      id={"worksiteTrendChart"}
                      data={trend}
                      intl={intl}
                      timeUnit={timeUnit}
                    />
                  </div>
                ) : (
                  <div className="p-8">
                    <Placeholder height="h-60" />
                  </div>
                )}
              </div>
              <div className="w-full lg:w-1/3">
                {geo ? (
                  <UsersHeatMap
                    id="UsersHeatMap"
                    data={geo.map((i) => ({
                      value: i.num_worksites,
                      id: "IT-" + i.cod_province,
                    }))}
                  />
                ) : (
                  <div className="p-8">
                    <Placeholder height="h-60" />
                  </div>
                )}
              </div>
            </div>
          </PageSection>
          <div className="flex flex-row grid grid-cols-3">
            <div className="col-span-2">
              <SankeyChart id="kpiWorksiteSankey" data={sankey} intl={intl} />
            </div>
            <div className="col-span-1">
              <h4 className="flex justify-between items-center text-lg leading-6 font-medium text-black-500">
                <span>
                  {intl.formatMessage({
                    id: "lost_worksites_reasons",
                    defaultMessage: "Cantieri persi: motivazioni",
                  })}
                </span>
              </h4>
              <KpiWorksiteDonut
                id="kpiLostWorksiteDonut"
                data={lostWorksiteData}
                intl={intl}
              />
            </div>
          </div>
          <div className="mt-8">
            <WorksitesDetailTable
              filtering={false}
              intl={intl}
              push={push}
              countryApiRequest={countryApiRequest}
              analysisPeriod={analysisPeriod}
              internalUsers={internalUsers}
              selectedStatus={selectedStatus}
              selectedOwnership={selectedOwnership}
              visibilityUserId={visibilityUserId}
              worksiteTypes={worksiteTypes}
              services={services}
              address={filterAddress}
              city={filterCity}
              cod_province={filterProvince}
              cod_worksite_type={filterWorksiteType.map((i) => i.value)}
              filterServices={filterServices.map((i) => i.value)}
            />
          </div>
        </div>
      ) : (
        <div className="flex flex-col w-full mb-4 gap-y-5">
          <div className="flex flex-row gap-x-5">
            <Placeholder width="w-1/2" height="h-60" />
            <Placeholder width="w-1/2" height="h-60" />
          </div>
          <div className="flex flex-row gap-x-5">
            <Placeholder width="w-1/2" height="h-60" />
            <Placeholder width="w-1/2" height="h-60" />
          </div>
        </div>
      )}
    </div>
  );
}
